"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * /guides — the public Guide Center hub. Lists every published guide as a
 * card; card titles/subtitles/read-times reuse each guide's own hero keys
 * (guides.<id>.hero.*), so guide copy is written exactly once and the hub
 * can never drift from the article. Same standalone-route pattern as the
 * guide pages themselves (SaveYourDayPage.tsx): public, no auth, no
 * dependency on the landing page's components beyond the shared brand
 * pieces.
 */

import { useEffect } from "react"
import { Link } from "react-router-dom"
import { GLOSSARY_LINKS } from "@bitebalance/i18n"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { BrandMarkV2 } from "@/components/landing/BrandMarkV2"
import { useI18n } from "@/i18n"
import { PageEntrance } from "@/components/PageEntrance"

/** Route + hero-key id for every published guide, newest first. Adding a
 * guide = one entry here + its own route/page/i18n block. */
export const GUIDE_INDEX = [
  { path: "/guides/how-your-grocery-budget-works", heroKey: "guides.howYourGroceryBudgetWorks.hero" },
  { path: "/guides/why-the-scale-lies", heroKey: "guides.whyTheScaleLies.hero" },
  { path: "/guides/save-your-day", heroKey: "guides.saveYourDay.hero" },
] as const

/** Budget Model v2 (ADR-052 §12.1): the Glossary is a reference, not a
 * guide — it has no hero/read-time, so it is rendered alongside GUIDE_INDEX
 * (which keeps the hero-key pattern intact for real guides). */
export const GLOSSARY_CARD = {
  path: GLOSSARY_LINKS.webPath,
  titleKey: "views.budget.glossaryHubCardTitle",
  subtitleKey: "views.budget.glossaryHubCardSubtitle",
} as const

export default function GuideHubPage() {
  const { t } = useI18n()

  useEffect(() => {
    const previousTitle = document.title
    document.title = t("guides.hub.meta.title")
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute("content") ?? null
    meta?.setAttribute("content", t("guides.hub.meta.description"))
    return () => {
      document.title = previousTitle
      if (previousDescription !== null) meta?.setAttribute("content", previousDescription)
    }
  }, [t])

  return (
    <PageEntrance>
      <div className="min-h-screen bg-cream text-ink">
        <a
          href="#main"
          className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
        >
          {t("nav.skipToContent")}
        </a>

        <header className="border-b border-line bg-paper">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-semibold text-ink-2 hover:text-ink"
            >
              <BrandMarkV2 size={22} />
              <span>← {t("guides.hub.backToHome")}</span>
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <main id="main">
          <div className="mx-auto max-w-3xl px-4 py-14 md:px-8 md:py-20">
            <p className="text-sm font-semibold tracking-wide text-green uppercase">
              {t("guides.hub.eyebrow")}
            </p>
            <h1 className="mt-3 text-4xl leading-tight font-black text-ink md:text-5xl">
              {t("guides.hub.title")}
            </h1>
            <p className="mt-5 max-w-prose text-lg text-ink-2">{t("guides.hub.subtitle")}</p>

            <div className="mt-12 flex flex-col gap-6">
              {GUIDE_INDEX.map(guide => (
                <Link
                  key={guide.path}
                  to={guide.path}
                  className="group flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-6 transition-all hover:-translate-y-0.5 hover:border-green/60 hover:shadow-[var(--bb-shadow)] md:p-8"
                >
                  <p className="text-xs font-semibold tracking-wide text-green uppercase">
                    {t(`${guide.heroKey}.readTime`)}
                  </p>
                  <h2 className="text-2xl leading-snug font-bold text-ink md:text-3xl">
                    {t(`${guide.heroKey}.title`)}
                  </h2>
                  <p className="max-w-prose text-base leading-relaxed text-ink-2">
                    {t(`${guide.heroKey}.subtitle`)}
                  </p>
                  <span className="mt-1 text-sm font-semibold text-green group-hover:underline">
                    {t("guides.hub.readCta")} →
                  </span>
                </Link>
              ))}

              <Link
                to={GLOSSARY_CARD.path}
                className="group flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-6 transition-all hover:-translate-y-0.5 hover:border-green/60 hover:shadow-[var(--bb-shadow)] md:p-8"
              >
                <h2 className="text-2xl leading-snug font-bold text-ink md:text-3xl">
                  {t(GLOSSARY_CARD.titleKey)}
                </h2>
                <p className="max-w-prose text-base leading-relaxed text-ink-2">
                  {t(GLOSSARY_CARD.subtitleKey)}
                </p>
                <span className="mt-1 text-sm font-semibold text-green group-hover:underline">
                  {t("guides.hub.readCta")} →
                </span>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </PageEntrance>
  )
}
